// Custom hook for IP scanning functionality
import { useState, useCallback, useRef } from 'react';
import { ipScannerService } from '../services';
import type { MinerDevice, ScanProgress, ScanResult } from '../services';

export interface UseIPScannerReturn {
  // State
  isScanning: boolean;
  scanProgress: ScanProgress | null;
  scanResult: ScanResult | null;
  miners: MinerDevice[];
  error: string | null;
  
  // Actions
  startScan: () => Promise<void>;
  stopScan: () => void;
  clearResults: () => void;
}

export const useIPScanner = (): UseIPScannerReturn => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [miners, setMiners] = useState<MinerDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const scanTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const startScan = useCallback(async () => {
    if (isScanning) {
      console.warn('Scan already in progress');
      return;
    }

    try {
      setIsScanning(true);
      setError(null);
      setScanProgress(null);
      setScanResult(null);
      setMiners([]);

      console.log('🚀 Starting network scan for miners...');

      // Set a maximum scan timeout (5 minutes)
      scanTimeoutRef.current = setTimeout(() => {
        console.warn('⏰ Scan timeout reached, stopping scan...');
        ipScannerService.stopScan();
        setIsScanning(false);
        setScanProgress(null);
      }, 5 * 60 * 1000);

      const result = await ipScannerService.scanNetworkWithProgress((progress) => {
        setScanProgress(progress);
        console.log(`📊 Scan progress: ${progress.scannedCount}/${progress.totalIps} (${progress.foundMiners} miners found)`);
      });

      // Clear timeout since scan completed successfully
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }

      setScanResult(result);
      setMiners(result.miners);
      
      console.log(`✅ Scan completed successfully! Found ${result.miners.length} miners`);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('❌ Scan failed:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsScanning(false);
      setScanProgress(null);
      
      // Clear timeout
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    }
  }, [isScanning]);

  const stopScan = useCallback(() => {
    console.log('🛑 Stopping scan...');
    ipScannerService.stopScan();
    setIsScanning(false);
    setScanProgress(null);
    
    // Clear timeout
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
  }, []);

  const clearResults = useCallback(() => {
    setScanResult(null);
    setMiners([]);
    setError(null);
    setScanProgress(null);
  }, []);

  return {
    isScanning,
    scanProgress,
    scanResult,
    miners,
    error,
    startScan,
    stopScan,
    clearResults
  };
};